import React, { useState } from 'react';
import { AlertTriangle, Clock, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { SubscriptionRenewal } from '@/lib/api/services/subscriptions';
import { RENEWAL_BANNER_CLASSES, renewalBannerText } from '@/lib/billing/renewal';
import { useSubscriptionRenewal } from '@/hooks/useSubscriptionRenewal';

interface SubscriptionRenewalBannerProps {
  /** An actionable renewal (see actionableRenewal) — the caller decides to mount. */
  renewal: SubscriptionRenewal;
  /** Called after the user closes it; the caller stops rendering it. */
  onDismiss?: () => void;
  /** "‹ 1/2 ›" controls when shown inside the top-of-app banner carousel. */
  carouselControls?: React.ReactNode;
}

/** Dismissal lasts until the day count changes (i.e. it comes back tomorrow). */
const dismissKey = (renewal: SubscriptionRenewal) =>
  `renewal-banner-dismissed:${renewal.subscription_id}:${renewal.days_left}`;

/** Closed today? The last day (critical) can never be closed. */
export function isRenewalBannerDismissed(renewal: SubscriptionRenewal): boolean {
  if (renewal.severity === 'critical') return false;
  try {
    return window.localStorage.getItem(dismissKey(renewal)) === '1';
  } catch {
    return false;
  }
}

/**
 * Top-of-app strip for the last 7 days of a manually paid plan. The severity
 * (colour) and days left come from the server's `renewal` verdict. The last
 * day can't be dismissed. After expiry the full-screen billing gate takes
 * over, so this strip is not shown then.
 */
export const SubscriptionRenewalBanner: React.FC<SubscriptionRenewalBannerProps> = ({
  renewal,
  onDismiss,
  carouselControls,
}) => {
  const navigate = useNavigate();
  const { startRenewal, renewing } = useSubscriptionRenewal();
  const [dismissed, setDismissed] = useState(() => isRenewalBannerDismissed(renewal));

  const severity = renewal.severity || 'notice';
  const canDismiss = severity !== 'critical';
  if (dismissed && canDismiss) return null;

  const onLight = severity === 'notice';

  return (
    <div
      className={`w-full ${RENEWAL_BANNER_CLASSES[severity]} px-4 py-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between z-50`}
      data-testid="subscription-renewal-banner"
      data-severity={severity}
      role="status"
    >
      <div className="flex items-center gap-2 min-w-0">
        {severity === 'critical' ? (
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        ) : (
          <Clock className="h-4 w-4 flex-shrink-0" />
        )}
        <span className="text-sm font-medium">{renewalBannerText(renewal)}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          variant="outline"
          className={
            onLight
              ? 'h-8 text-xs font-semibold bg-transparent border-amber-700/40 hover:bg-amber-200/60 dark:border-amber-200/40 dark:hover:bg-amber-800'
              : 'h-8 text-xs font-semibold bg-transparent border-white/60 text-white hover:bg-white/15 hover:text-white'
          }
          onClick={() => navigate('/home/account-settings?section=plans')}
          data-testid="subscription-renewal-banner-choose-plan"
        >
          Choose Plan
        </Button>
        <Button
          size="sm"
          className={
            onLight
              ? 'h-8 text-xs font-semibold bg-amber-900 text-white hover:bg-amber-800 dark:bg-amber-100 dark:text-amber-950 dark:hover:bg-white'
              : 'h-8 text-xs font-semibold bg-white text-gray-900 hover:bg-gray-100'
          }
          onClick={startRenewal}
          disabled={renewing}
          data-testid="subscription-renewal-banner-renew"
        >
          {renewing && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
          Renew Now
        </Button>
        {carouselControls}
        {canDismiss && (
          <button
            onClick={() => {
              setDismissed(true);
              try {
                window.localStorage.setItem(dismissKey(renewal), '1');
              } catch {
                /* storage blocked — dismissed for this view only */
              }
              onDismiss?.();
            }}
            className={`p-1 rounded-full transition-colors ${onLight ? 'hover:bg-amber-200/70 dark:hover:bg-amber-800' : 'hover:bg-white/15'}`}
            aria-label="Dismiss renewal reminder"
            data-testid="subscription-renewal-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
